import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { INewKnmPlan } from 'APIPackage/PlanListModule/interfaces/INewKnmPlan';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getUserKnoSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { planListCreatePlan } from 'PlanListPackage/MainModule/actions/planListActions';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { ROUTE_KNM_LIST_WITH_PLAN, ROUTE_PLAN_BY_ID } from 'CorePackage/MainModule/routes/routes';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';

import { initialCreatePlanForm } from '../constants/initialCreatePlanForm';
import styles from './CreatePlanModal.module.scss';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  getDictionariesSupervisionTypesSelectOptionsSelector,
  getPlanTypeWithFederalLawSelectOptions,
} from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import {
  dictionariesFilteredFederalLawsGet,
  dictionariesKnoOrganizationsItemGet,
  dictionariesSupervisionTypesItemGet,
} from '../../../CorePackage/DictionariesModule/actions/dictionariesActions';
import {
  getAccountOrganizationsData,
  getAccountProsecutorOrganizationsWithDistrictSelectOptions,
} from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { CheckNotificationModal } from '../../../CorePackage/MainModule/components/CheckNotificationModal';
import { CheckNotificationTypesEnum } from '../../../CorePackage/MainModule/enums/CheckNotificationTypesEnum';
import { isGeneralProsecutorOGRN } from '../../../CorePackage/MainModule/validators/isOgrn';
import { FEDERAL_LOW_248_GUID } from '../../../KnmPackage/MainModule/constants/erknmFederalLawUuid';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IPlanTypeWithFederalLaw } from '../../../ImportPackage/MainModule/interfaces/IPlanTypeWithFederalLaw';

export interface ICreatePlanForm {
  endDate: string;
  kindControl?: IDictionarySupervisionTypeSelectOption;
  kno?: IDictionaryKnoOrganizationSelectOption;
  prosecutor?: IUserOrganisationSelectOption;
  startDate: string;
  type?: IDictionarySelectOption<IPlanTypeWithFederalLaw>;
}

export const FZ248 = 'FZ248';

interface ICreatePlanModalProps {
  federalLawsWithPlanTypeSelectOptions: IDictionarySelectOption<IPlanTypeWithFederalLaw>[] | undefined;
  isErknm: boolean;
  knoOrganizationsSelectOptions: IDictionaryKnoOrganizationSelectOption[] | undefined;
  knos: IUserOrganisationSelectOption[] | undefined;
  onClose: () => void;
  prosecutors: IUserOrganisationSelectOption[] | undefined;
  supervisionTypesSelectOptionsSelector: (
    knoOrganizationId: string | undefined,
    isUseTransferringAuthorities: boolean,
    federalLawUUIDs: string[],
    isPlanned?: boolean,
  ) => IDictionarySupervisionTypeSelectOption[] | undefined;
  userKnoOrganisations: IUserOrganisation[];
  visible?: boolean;
}

export const CreatePlanModalWithoutStore = ({
  federalLawsWithPlanTypeSelectOptions,
  isErknm,
  knoOrganizationsSelectOptions,
  knos,
  onClose,
  prosecutors,
  supervisionTypesSelectOptionsSelector,
  userKnoOrganisations,
  visible,
}: ICreatePlanModalProps) => {
  const [defaultPlanType, setDefaultPlanType] = useState<string | undefined>(undefined);
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState<boolean>(false);
  const [formData, setFormData] = useState<ICreatePlanForm | undefined>();
  const [supervisionTypesFilter, setSupervisionTypesFilter] = useState<{
    federalLawUUID: string;
    knoId: string;
  }>();

  const getSuccessLink = useCallback(
    (id: number) => (
      <div>
        План <Link to={isErknm ? ROUTE_PLAN_BY_ID(id) : ROUTE_KNM_LIST_WITH_PLAN(id)}>{id}</Link> успешно создан.
      </div>
    ),
    [isErknm],
  );

  const supervisionTypesSelectOptions = useMemo(() => {
    if (!supervisionTypesFilter) {
      return undefined;
    }

    return supervisionTypesSelectOptionsSelector(
      supervisionTypesFilter.knoId,
      false,
      [supervisionTypesFilter.federalLawUUID],
      true,
    );
  }, [supervisionTypesFilter, supervisionTypesSelectOptionsSelector]);

  /**
   * Функция создания плана
   */
  const createPlan = useCallback(
    (data: ICreatePlanForm) => {
      const newPlan: INewKnmPlan = {
        ervkProsecutorOrgUuid:
          data?.prosecutor?.ervkUuid?.toString() ||
          prosecutors?.find(item => item.byDefault)?.ervkUuid?.toString() ||
          '',
        ervkProsecutorOrgVersionUuid:
          data?.prosecutor?.ervkVersionUuid?.toString() ||
          prosecutors?.find(item => item.byDefault)?.ervkVersionUuid?.toString() ||
          '',
        prosecutorOrgName: data.prosecutor?.value || prosecutors?.find(item => item.byDefault)?.fullName || '',
        endDate: formatDate({ date: data.endDate, dateFormat: DATE_FORMAT_ISO_FNS }),
        startDate: formatDate({ date: data.startDate, dateFormat: DATE_FORMAT_ISO_FNS }),
        knoId: data.kno?.code || '',
        knoName: data.kno?.fullName || '',
        type: data.type?.label || '',
        creationType: isErknm ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP,
        knmPlanData: {
          version: isErknm ? KnmVersionsEnum.ERKNM : KnmVersionsEnum.ERP,
          supervisionTypeErknm: data.kindControl?.value || undefined,
          knoOrganizationErknm: data.kno?.value || undefined,
        },
      };

      dispatch(planListCreatePlan(newPlan, getSuccessLink, onClose));
    },
    [
      dispatch,
      getSuccessLink,
      isErknm,
      knoOrganizationsSelectOptions,
      onClose,
      federalLawsWithPlanTypeSelectOptions,
      prosecutors,
    ],
  );

  /**
   * Функция обработки значений формы и вызова создания плана
   */
  const onSubmit = useCallback(
    (data: ICreatePlanForm) => {
      const isGeneralProsecutor = isGeneralProsecutorOGRN(data.prosecutor?.ogrn);

      const isTopLevel = knos?.find(i => i.id === String(data.kno?.idStr))?.isTopLevel;

      if (isGeneralProsecutor && !isTopLevel) {
        setFormData(data);
        setIsNotificationModalVisible(true);
      } else {
        createPlan(data);
      }
    },
    [createPlan, knos],
  );

  useEffect(() => {
    if (!knoOrganizationsSelectOptions) {
      dispatch(dictionariesKnoOrganizationsItemGet());
    }
  }, [knoOrganizationsSelectOptions]);

  useEffect(() => {
    if (isErknm) {
      setDefaultPlanType(FZ248);
    }
  }, [isErknm]);

  /**
   * Функция подтверждения ознакомления с уведомлением
   */
  const onClickButtonApproveNotification = useCallback(() => {
    if (formData) {
      setIsNotificationModalVisible(false);
      createPlan(formData);
    }
  }, [formData]);

  const loadSupervisionTypes = useCallback(
    (kno?: IDictionaryKnoOrganizationSelectOption, type?: IDictionarySelectOption<IPlanTypeWithFederalLaw>) => {
      const knoId = kno?.value?.dictVersionId;

      if (!knoId) {
        return;
      }

      let federalLawUUID: string | undefined;

      if (isErknm) {
        federalLawUUID = FEDERAL_LOW_248_GUID;
      } else {
        federalLawUUID = type?.value?.dictVersionId;
      }

      if (!federalLawUUID) {
        return;
      }

      setSupervisionTypesFilter({ knoId, federalLawUUID });
      dispatch(dictionariesSupervisionTypesItemGet([knoId], true, [federalLawUUID]));
    },
    [defaultPlanType, dispatch, isErknm],
  );

  const onChangeKno = useCallback(
    (option: IDictionaryKnoOrganizationSelectOption, form?: FormRenderProps<ICreatePlanForm>['form']) => {
      form?.change('kindControl', undefined);
      form?.change('type', undefined);
      setSupervisionTypesFilter(undefined);
      if (isErknm) {
        loadSupervisionTypes(
          option,
          federalLawsWithPlanTypeSelectOptions?.find(item => item?.planTypeId === defaultPlanType),
        );
      } else {
        dispatch(dictionariesFilteredFederalLawsGet(option.value?.dictVersionId, false));
      }
    },
    [defaultPlanType, dispatch, federalLawsWithPlanTypeSelectOptions, isErknm, loadSupervisionTypes],
  );

  const onChangeType = useCallback(
    (
      option: IDictionarySelectOption<IPlanTypeWithFederalLaw>,
      kno?: IDictionaryKnoOrganizationSelectOption,
      form?: FormRenderProps<ICreatePlanForm>['form'],
    ) => {
      form?.change('kindControl', undefined);
      loadSupervisionTypes(kno, option);
    },
    [loadSupervisionTypes],
  );

  const defaultKno = useMemo(() => {
    if (knoOrganizationsSelectOptions && userKnoOrganisations) {
      const byDefaultKno = userKnoOrganisations.find(i => i.byDefault);

      if (byDefaultKno) {
        return knoOrganizationsSelectOptions.find(i => i.idStr?.toString() === byDefaultKno.id);
      }
    }

    return undefined;
  }, [knoOrganizationsSelectOptions, userKnoOrganisations]);

  const hasInitializedDefaultKnoRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      hasInitializedDefaultKnoRef.current = false;
      setSupervisionTypesFilter(undefined);

      return;
    }

    if (!defaultKno || hasInitializedDefaultKnoRef.current) {
      return;
    }

    if (isErknm && (!defaultPlanType || !federalLawsWithPlanTypeSelectOptions)) {
      return;
    }

    hasInitializedDefaultKnoRef.current = true;

    if (isErknm) {
      loadSupervisionTypes(
        defaultKno,
        federalLawsWithPlanTypeSelectOptions?.find(item => item?.planTypeId === defaultPlanType),
      );
    } else {
      dispatch(dictionariesFilteredFederalLawsGet(defaultKno.value?.dictVersionId, false));
    }
  }, [
    visible,
    defaultKno,
    defaultPlanType,
    dispatch,
    federalLawsWithPlanTypeSelectOptions,
    isErknm,
    loadSupervisionTypes,
  ]);

  const renderForm = useCallback(
    ({ handleSubmit, values, form }: FormRenderProps<ICreatePlanForm>) => (
      <form onSubmit={handleSubmit}>
        <ModalHeader size="large" title="Создание плана КНМ" />
        <ModalBody size="content" border="bottom">
          <Row>
            <FieldBlock label="Орган прокуратуры" color="black">
              <SelectInputField
                id="prosecutor"
                name="prosecutor"
                size="medium"
                isClearable={false}
                defaultValue={prosecutors?.find(item => item.byDefault)}
                options={prosecutors}
                disabled={false}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Контрольный (надзорный) орган" color="black">
              <SelectInputField
                id="kno"
                name="kno"
                size="medium"
                isClearable={false}
                options={knoOrganizationsSelectOptions}
                defaultValue={defaultKno}
                onChange={option => onChangeKno(option, form)}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Тип плана КНМ" color="black">
              <SelectInputField
                id="type"
                name="type"
                size="medium"
                isClearable={false}
                disabled={isErknm}
                defaultValue={
                  isErknm
                    ? federalLawsWithPlanTypeSelectOptions?.find(item => item?.planTypeId === defaultPlanType)
                    : undefined
                }
                options={federalLawsWithPlanTypeSelectOptions}
                onChange={option => onChangeType(option, values.kno, form)}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Вид контроля (надзора)" color="black">
              <SelectInputField
                id="kindControl"
                name="kindControl"
                key={supervisionTypesFilter?.knoId ?? 'empty'}
                options={supervisionTypesSelectOptions}
                defaultValue={supervisionTypesSelectOptions ? supervisionTypesSelectOptions[0] : undefined}
                isSearchable
                className={styles.FullWidth}
                noOptionsMessage="Для выбранного КНО и ФЗ по данным ЕРВК отсутствует вид контроля для плановых мероприятий"
              />
            </FieldBlock>
          </Row>
          <Row notWrap>
            <FieldBlock label="Период проведения" color="black">
              <Row notWrap notSpace margin="none">
                <DatePickerField id="startDate" name="startDate" className="shared-field" disabled />
                <div className="shared-divider">-</div>
                <DatePickerField id="endDate" name="endDate" className="shared-field" disabled />
              </Row>
            </FieldBlock>
          </Row>
        </ModalBody>
        <ModalActions>
          <Button text="Создать" id="createButton" color="primary" size="medium" type="submit" />
          <Button text="Закрыть" size="medium" type="button" onClick={onClose} />
        </ModalActions>
      </form>
    ),
    [
      defaultKno,
      defaultPlanType,
      isErknm,
      knoOrganizationsSelectOptions,
      supervisionTypesSelectOptions,
      supervisionTypesFilter?.knoId,
      onChangeKno,
      onChangeType,
      onClose,
      prosecutors,
      federalLawsWithPlanTypeSelectOptions,
    ],
  );

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={visible} onClose={onClose}>
      <Form<ICreatePlanForm> onSubmit={onSubmit} render={renderForm} initialValues={initialCreatePlanForm} />
      <CheckNotificationModal
        notificationType={CheckNotificationTypesEnum.FOR_CREATE_PLAN_WITH_GENERAL_PROSECUTOR}
        visible={isNotificationModalVisible}
        onConfirm={onClickButtonApproveNotification}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  federalLawsWithPlanTypeSelectOptions: getPlanTypeWithFederalLawSelectOptions(state),
  isErknm: state.core.lawInterface === LawInterfacesEnum.FZ_248,
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  knos: getUserKnoSelectOptions(state),
  prosecutors: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
  supervisionTypesSelectOptionsSelector: getDictionariesSupervisionTypesSelectOptionsSelector(state),
  userKnoOrganisations: getAccountOrganizationsData(state).kno,
});

export const CreatePlanModal = connect(mapStateToProps)(CreatePlanModalWithoutStore);
